import React, { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import ProblemChart from './ProblemChart';

interface ProblemSelectorProps {
  handleCTAClick: (e: React.MouseEvent) => void;
}

const ProblemSelector: React.FC<ProblemSelectorProps> = ({ handleCTAClick }) => {
  const [selectedProblem, setSelectedProblem] = useState<string>("");
  const [showSolution, setShowSolution] = useState<boolean>(false);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProblem(e.target.value);
    setShowSolution(false);
  };

  const handleShowSolution = () => {
    if (selectedProblem) {
      setShowSolution(true);
    }
  };

  const solutions = {
    "Krankheitsausfälle reduzieren": {
      title: "Gesundheitsvorsorge systematisch implementieren",
      description: "Durch ein ganzheitliches Benefits-Werk können Sie Krankheitsausfälle um bis zu 30% reduzieren. Wir bieten ein maßgeschneidertes Programm mit präventiven Maßnahmen, Gesundheitsbudgets und flexiblen Unterstützungsleistungen, das die Gesundheit Ihrer Mitarbeiter nachhaltig fördert.",
    },
    "Mitarbeitende binden": {
      title: "Emotionale Bindung durch spürbare Benefits schaffen",
      description: "Unsere Analysen zeigen: Mitarbeiter bleiben länger, wenn sie sich wertgeschätzt fühlen. Mit unserem Beratungskonzept schaffen Sie emotionale Bindungsfaktoren und steigern die Zufriedenheit Ihrer Mitarbeiter nachweislich um bis zu 40% - und reduzieren damit Fluktuation und Rekrutierungskosten erheblich.",
    },
    "Top-Talente gewinnen": {
      title: "Als Wunscharbeitgeber positionieren",
      description: "Mit einem durchdachten Benefit-System werden Sie zum Magnet für qualifizierte Fachkräfte. Wir helfen Ihnen, Ihre Arbeitgebermarke strategisch zu positionieren und durch Alleinstellungsmerkmale im Bereich Mitarbeitergesundheit und -vorsorge einen entscheidenden Wettbewerbsvorteil im Kampf um die besten Talente zu erzielen.",
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl shadow-lg p-8 space-y-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">
          Was ist Ihre größte Herausforderung?
        </h3>
        
        <div className="space-y-4">
          <select
            value={selectedProblem}
            onChange={handleSelectChange}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition-all"
          >
            <option value="" disabled>Bitte wählen Sie...</option>
            <option value="Krankheitsausfälle reduzieren">Krankheitsausfälle reduzieren</option>
            <option value="Mitarbeitende binden">Mitarbeitende binden</option>
            <option value="Top-Talente gewinnen">Top-Talente gewinnen</option>
          </select>
          
          <button 
            onClick={handleShowSolution}
            disabled={!selectedProblem}
            className={`w-full btn ${selectedProblem ? 'btn-primary' : 'bg-gray-200 text-gray-500 cursor-not-allowed'} py-3 flex justify-center items-center gap-2`}
          >
            Lösung anzeigen
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      {/* Chart Component */}
      {selectedProblem && <ProblemChart selectedProblem={selectedProblem} />}

      {/* Solution Popup */}
      {showSolution && selectedProblem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button 
              onClick={() => setShowSolution(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
            
            <div className="mb-2">
              <span className="inline-block bg-secondary-100 text-primary-600 text-sm font-medium px-3 py-1 rounded-full mb-4">
                Ihre Herausforderung: {selectedProblem}
              </span>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {solutions[selectedProblem as keyof typeof solutions]?.title}
              </h3>
              <p className="text-gray-700 mb-6">
                {solutions[selectedProblem as keyof typeof solutions]?.description}
              </p>
              <button 
                onClick={handleCTAClick}
                className="btn btn-primary w-full py-3 flex justify-center items-center gap-2"
              >
                Kostenloses Beratungsgespräch vereinbaren
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemSelector;